"use client";

import { cn } from "@/lib/utils";
import type { Participant } from "../base-match-template";
import {
  CourtSvg,
  courtLines,
  courtQuad,
  createCourtProjector,
  getRacketMatchState,
  PAINT_LINE,
  PropShadow,
  RacketMatchFrame,
  RacketScore,
  useSvgIdPrefix,
} from "./racket-court-kit";

export type PickleballCourtPreviewProps = {
  mode: "singles" | "doubles";
  yourTeam: Participant[];
  opponentTeam: Participant[];
  yourWon?: boolean;
  showLabels?: boolean;
  linkToProfiles?: boolean;
  matchScore?: { yourGames: number; opponentGames: number };
  games?: Array<{ yourScore: number; opponentScore: number }>;
};

// ─── Court geometry (outdoor acrylic court at golden hour) ──────────────────
// 13.41m × 6.10m: the non-volley zone ("kitchen") runs 2.13m off the net,
// centre lines from the kitchen line to each baseline.

const P = createCourtProjector({
  yFar: 60,
  yNear: 196,
  farHalfWidth: 110,
  nearHalfWidth: 136,
});

const KITCHEN = 0.341;

const COURT = courtQuad(P, 0, 0, 1, 1);
const KITCHEN_ZONE = courtQuad(P, KITCHEN, 0, 1 - KITCHEN, 1);
const APRON = courtQuad(P, -0.11, -0.2, 1.11, 1.25);
const LINES = courtLines(P, [
  [0, 0, 1, 0],
  [1, 0, 1, 1],
  [1, 1, 0, 1],
  [0, 1, 0, 0],
  [KITCHEN, 0, KITCHEN, 1],
  [1 - KITCHEN, 0, 1 - KITCHEN, 1],
  [0, 0.5, KITCHEN, 0.5],
  [1 - KITCHEN, 0.5, 1, 0.5],
]);
// Low sun from the right: the net throws a long shadow toward the left.
const NET_SHADOW = courtQuad(P, 0.47, -0.05, 0.5, 1.05);
const [, NET_FAR_Y] = P(0.5, -0.05);
const [, NET_NEAR_Y] = P(0.5, 1.05);
const FENCE_POSTS = [8, 58, 108, 192, 242, 292];

function PickleballCourtArt() {
  const id = useSvgIdPrefix();
  return (
    <>
      <CourtSvg>
        <defs>
          <linearGradient id={`${id}-sky`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#3b5f95" />
            <stop offset="0.55" stopColor="#e79a6d" />
            <stop offset="1" stopColor="#ffc982" />
          </linearGradient>
          <linearGradient id={`${id}-ground`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#3e8a55" />
            <stop offset="1" stopColor="#1c5233" />
          </linearGradient>
          <linearGradient id={`${id}-court`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#3a7fd0" />
            <stop offset="0.6" stopColor="#2563b3" />
            <stop offset="1" stopColor="#1a4b8e" />
          </linearGradient>
          <pattern
            id={`${id}-chain`}
            width="3"
            height="3"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M0,1.5L1.5,0L3,1.5L1.5,3Z"
              fill="none"
              stroke="rgba(30,40,40,0.55)"
              strokeWidth="0.35"
            />
          </pattern>
        </defs>

        {/* Golden-hour sky, tree line, chain-link fence + windscreen. */}
        <rect width="300" height="60" fill={`url(#${id}-sky)`} />
        <path
          d="M0,30 C12,18 24,22 32,26 C40,14 58,16 66,26 C76,20 90,22 98,30 L98,60 L0,60Z M202,30 C212,18 226,20 234,26 C244,14 262,16 270,24 C282,18 294,22 300,28 L300,60 L202,60Z"
          fill="#1f3d2c"
        />
        <path
          d="M98,34 C110,28 122,30 130,34 L170,34 C178,30 192,28 202,34 L202,60 L98,60Z"
          fill="#2a4c35"
        />
        <rect y="12" width="300" height="28" fill={`url(#${id}-chain)`} />
        <rect y="38" width="300" height="20" fill="#174a32" />
        <rect y="38" width="300" height="1.2" fill="rgba(255,220,170,0.55)" />
        {[30, 80, 200, 250].map((x) => (
          <rect
            key={x}
            x={x - 16}
            y="45"
            width="32"
            height="5"
            rx="1"
            fill="rgba(230,250,220,0.3)"
          />
        ))}
        {FENCE_POSTS.map((x) => (
          <rect key={x} x={x - 1} y="10" width="2" height="48" fill="#25302d" />
        ))}
        <rect y="10" width="300" height="1.6" fill="#25302d" />

        {/* Green surround, blue court, lighter kitchen. */}
        <rect y="58" width="300" height="162" fill={`url(#${id}-ground)`} />
        <path d={APRON} fill="rgba(255,255,255,0.04)" />
        <path d={COURT} fill={`url(#${id}-court)`} />
        <path d={KITCHEN_ZONE} fill="rgba(140,215,255,0.16)" />
        <path
          d={LINES}
          {...PAINT_LINE}
          stroke="rgba(255,255,255,0.94)"
          strokeWidth="1.6"
        />

        {/* Net with its long evening shadow, centre strap, posts. */}
        <path d={NET_SHADOW} fill="rgba(4,18,40,0.28)" />
        <rect
          x="148.4"
          y={NET_FAR_Y - 6}
          width="3.2"
          height={NET_NEAR_Y - NET_FAR_Y - 6}
          fill="rgba(14,18,24,0.7)"
        />
        <rect
          x="148.4"
          y={NET_FAR_Y - 6}
          width="1.3"
          height={NET_NEAR_Y - NET_FAR_Y - 6}
          fill="#f6f2ea"
        />
        <rect
          x="147"
          y={NET_FAR_Y - 9}
          width="6"
          height="9"
          rx="1"
          fill="#20262c"
        />
        <rect
          x="145.6"
          y={NET_NEAR_Y - 18}
          width="8.8"
          height="17"
          rx="1.4"
          fill="#20262c"
        />
        <rect
          x="151.2"
          y={NET_NEAR_Y - 18}
          width="3.2"
          height="17"
          rx="1"
          fill="rgba(255,200,140,0.4)"
        />
      </CourtSvg>

      {/* Lighting: low sun from the right, warm rim along the court. */}
      <div className="absolute inset-x-0 top-0 h-[60px] bg-[radial-gradient(ellipse_30%_90%_at_86%_100%,rgba(255,226,170,0.55),transparent_70%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_70%_at_78%_38%,rgba(255,196,120,0.24),transparent_70%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_58%,rgba(6,20,24,0.38)_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_90%_110%_at_50%_45%,transparent_55%,rgba(0,0,0,0.38)_100%)]" />
    </>
  );
}

const PICKLEBALL_HOLES = [
  { top: "16%", left: "38%" },
  { top: "16%", left: "62%" },
  { top: "38%", left: "22%" },
  { top: "38%", left: "50%" },
  { top: "38%", left: "76%" },
  { top: "62%", left: "34%" },
  { top: "62%", left: "62%" },
  { top: "82%", left: "48%" },
];

const Pickleball = ({ className }: { className?: string }) => (
  <div
    className={cn(
      "absolute overflow-hidden rounded-full bg-[radial-gradient(circle_at_34%_30%,#fff6a8_0%,#ffe03a_40%,#e0a800_100%)] shadow-[inset_-1.5px_-2px_3px_rgba(0,0,0,0.28),0_0_10px_rgba(255,214,60,0.35)]",
      className,
    )}
  >
    {PICKLEBALL_HOLES.map((hole) => (
      <div
        key={`${hole.top}-${hole.left}`}
        className="absolute h-[16%] w-[16%] -translate-x-1/2 rounded-full bg-[#8a5d00]/45"
        style={hole}
      />
    ))}
  </div>
);

/** A composite paddle: rounded-rectangle face, wrapped grip. */
const Paddle = ({ className }: { className?: string }) => (
  <div className={cn("absolute h-[44px] w-[26px]", className)}>
    <div className="absolute top-[28px] left-1/2 h-[16px] w-[8px] -translate-x-1/2 rounded-b-[3px] bg-[repeating-linear-gradient(160deg,#1d2430_0px,#1d2430_2px,#3a4658_2px,#3a4658_3px)]" />
    <div className="absolute top-0 left-0 h-[32px] w-[26px] rounded-[10px_10px_8px_8px] border-[1.5px] border-[#1d2430] bg-[linear-gradient(140deg,#ff8a3d,#ff4f6a_55%,#7b3cff)] shadow-[inset_-2px_-3px_5px_rgba(0,0,0,0.25)]" />
  </div>
);

export function PickleballCourtPreview({
  mode: _mode,
  yourTeam,
  opponentTeam,
  yourWon,
  linkToProfiles = false,
  matchScore,
  games,
}: PickleballCourtPreviewProps) {
  const opponentWon = opponentTeam.length > 0 && yourWon === false;
  const yourTeamWon = !!yourWon && !opponentWon;

  const hasScore =
    !!matchScore &&
    (matchScore.yourGames > 0 ||
      matchScore.opponentGames > 0 ||
      (games?.length ?? 0) > 0);
  const matchState = getRacketMatchState(yourTeam, opponentTeam, hasScore);

  return (
    <RacketMatchFrame
      label="Pickleball"
      frameClassName="bg-[#1c5233]"
      matchState={matchState}
      scene={<PickleballCourtArt />}
      decor={
        matchState === "FULL" ? (
          <>
            <PropShadow className="top-[86%] left-[32%] h-[6px] w-[18px]" />
            <Pickleball className="top-[81%] left-[33%] h-[12px] w-[12px]" />
          </>
        ) : (
          <>
            {/* Paddle laid by the kitchen line, ball dinked over the net. */}
            <PropShadow className="top-[69%] left-[26%] h-[10px] w-[32px]" />
            <Paddle className="top-[55%] left-[28%] rotate-[58deg]" />
            <PropShadow className="top-[66%] left-[59%] h-[6px] w-[18px]" />
            <Pickleball className="top-[44%] left-[60%] h-[14px] w-[14px]" />
          </>
        )
      }
      yourTeam={yourTeam}
      opponentTeam={opponentTeam}
      yourWinner={yourTeamWon}
      opponentWinner={opponentWon}
      linkToProfiles={linkToProfiles}
      center={
        matchState === "FULL" && matchScore ? (
          <RacketScore
            left={matchScore.yourGames}
            right={matchScore.opponentGames}
            leftWinner={yourTeamWon}
            rightWinner={opponentWon}
            breakdown={games}
            accentClassName="bg-[#ffe03a] shadow-[0_0_12px_rgba(255,224,58,0.7)]"
          />
        ) : null
      }
    />
  );
}
